import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CrossClientQueryEvaluationResultStatuses,
  CrossClientQueryEvaluationStatuses,
  CrossClientQueryEvaluationTypes,
  type CrossClientQueryEvaluation,
  type CrossClientQueryEvaluationResultDetail,
  type CrossClientQueryResult,
} from '../../../../models/CrossClient'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import {
  bumpCrossClientResultsRevision,
  isTerminalEvaluationStatus,
  selectCrossClientActiveEvaluations,
  selectCrossClientResolversInFlight,
} from '../../../../store/crossClient/crossClientResults'
import { awaitsResolvers } from '../../../../store/crossClient/utils'
import { useLazyGetCrossClientEvaluationResultsQuery, useLazyGetCrossClientEvaluationsQuery } from '../../../../store/crossClient/crossClient'
import { useCancelCrossClientQueryEvaluationMutation } from '../../../../store/crossClient/crossClientExecution'
import { CcqCards, cardForResult, ccqOffersDeepAnalysis, type CcqCard } from './ccqResultView'
import { CcqAiBatchSteps, CcqAiContext, IDLE_BATCH, type CcqAi, type CcqAiBatch } from './useCcqAi'
import { useCcqRun } from './useCcqRun'
import { useCcqWorkspace } from './useCcqWorkspace'
import { useCrossClientEvaluationActions } from './useCrossClientEvaluationActions'
import { useRetryLater } from './useRetryLater'

const SUMMARY_MS = 4000

const ATTRIBUTION_PAGE_SIZE = 100

const summaryOf = (evaluation: CrossClientQueryEvaluation): CcqAiBatch => {
  const completed = evaluation.progress?.statusCounts.completed ?? 0
  const matched = evaluation.progress?.producedMatchCounts.full ?? 0
  const notMatched = evaluation.progress?.producedMatchCounts.noMatch ?? 0
  return {
    step: CcqAiBatchSteps.RESULT,
    // What it got through, not what it was given: a stopped batch only checked some.
    clientCount: completed,
    movedToMatched: matched,
    movedToNotMatched: notMatched,
    movedToMissingData: Math.max(completed - matched - notMatched, 0),
    isStopping: false,
  }
}

export const CcqAiProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { execution } = useCcqRun()
  const evaluations = useAppSelector(selectCrossClientActiveEvaluations)
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  const [cancelEvaluation] = useCancelCrossClientQueryEvaluationMutation()
  const [triggerEvaluationResults] = useLazyGetCrossClientEvaluationResultsQuery()
  const [triggerEvaluations] = useLazyGetCrossClientEvaluationsQuery()
  const { evaluateResults } = useCrossClientEvaluationActions(null)
  // Preview rows freeze once seeded; they catch up when a batch starts (rows turn "AI checking") and finishes.
  const { refreshPreviews } = useCcqWorkspace()
  const [cardByEvaluation, setCardByEvaluation] = useState<Record<string, CcqCard>>({})
  const [checking, setChecking] = useState<Record<string, string[]>>({})
  const [summaries, setSummaries] = useState<Partial<Record<CcqCard, CcqAiBatch>>>({})
  const [stoppingIds, setStoppingIds] = useState<string[]>([])
  // Guards a second Stop before the re-render: cancelling again fails, as the evaluation is no longer active.
  const stopRequestedRef = useRef(new Set<string>())

  const running = useMemo(
    () => evaluations.filter((each) => each.type === CrossClientQueryEvaluationTypes.AI && !isTerminalEvaluationStatus(each.status)),
    [evaluations],
  )

  const trackEvaluation = useCallback(
    (evaluation: CrossClientQueryEvaluation, card: CcqCard, resultIds: string[] = []) => {
      setCardByEvaluation((previous) => ({ ...previous, [evaluation.id]: card }))
      setChecking((previous) => ({ ...previous, [evaluation.id]: resultIds }))
      refreshPreviews()
      // The open full list too: a picker batch has no known ids to mark as checking.
      dispatch(bumpCrossClientResultsRevision())
    },
    [refreshPreviews, dispatch],
  )

  const awaitingCardRef = useRef(new Set<string>())
  // The lookup effect only reruns when the set of unattributed ids changes; a failed lookup re-arms it.
  const { retryToken: attributionRetry, retryLater: retryAttribution } = useRetryLater()

  const finishEvaluations = useCallback(
    (cards: Record<string, CcqCard>) => {
      const ids = Object.keys(cards)
      refreshPreviews()
      triggerEvaluations({ accountId, executionId: execution.id, types: [CrossClientQueryEvaluationTypes.AI], expand: ['progress'] })
        .unwrap()
        .then((response) => {
          const done = (response?.data ?? []).filter((each) => ids.includes(each.id) && (each.progress?.statusCounts.completed ?? 0) > 0)
          setSummaries((previous) => ({ ...previous, ...Object.fromEntries(done.map((each) => [cards[each.id], summaryOf(each)])) }))
        })
        .catch(console.error)
    },
    [refreshPreviews, triggerEvaluations, accountId, execution.id],
  )

  // Evaluations this page didn't start (a reload, another tab): an item not yet checked still sits in the
  // card the batch was started from; checked ones have moved on, and can fill whole pages first.
  const unattributed = running
    .filter((each) => cardByEvaluation[each.id] === undefined)
    .map((each) => each.id)
    .join(',')
  useEffect(() => {
    if (unattributed === '') {
      return
    }
    const findUnchecked = async (evaluationId: string, page: number): Promise<CrossClientQueryEvaluationResultDetail | undefined> => {
      const response = await triggerEvaluationResults({ accountId, executionId: execution.id, evaluationId, page, size: ATTRIBUTION_PAGE_SIZE }).unwrap()
      const unchecked = response.data.find(
        (item) => item.status === CrossClientQueryEvaluationResultStatuses.PENDING || item.status === CrossClientQueryEvaluationResultStatuses.PROCESSING,
      )
      if (unchecked !== undefined || response.page.number + 1 >= response.page.totalPages) {
        return unchecked
      }
      return findUnchecked(evaluationId, page + 1)
    }
    unattributed.split(',').forEach((evaluationId) => {
      findUnchecked(evaluationId, 0)
        .then((unchecked) => {
          const origin = unchecked ? cardForResult({ status: unchecked.resultStatus, match: unchecked.resultMatch }) : null
          const card = origin !== null && ccqOffersDeepAnalysis(origin) ? origin : CcqCards.CLOSER
          // The page may have tracked it meanwhile (it lands in the store before its starter hears back).
          setCardByEvaluation((previous) => (previous[evaluationId] === undefined ? { ...previous, [evaluationId]: card } : previous))
          if (awaitingCardRef.current.delete(evaluationId)) {
            finishEvaluations({ [evaluationId]: card })
          }
        })
        .catch((e) => {
          console.error(e)
          if (awaitingCardRef.current.delete(evaluationId)) {
            refreshPreviews()
            return
          }
          retryAttribution()
        })
    })
  }, [unattributed, attributionRetry, retryAttribution, triggerEvaluationResults, accountId, execution.id, finishEvaluations, refreshPreviews])

  // The poller drops a finished evaluation (or keeps its stale progress), so its final numbers are refetched for the summary.
  const runningIds = running.map((each) => each.id).join(',')
  const previousRunningRef = useRef<string[]>([])
  useEffect(() => {
    const current = runningIds === '' ? [] : runningIds.split(',')
    const finished = previousRunningRef.current.filter((id) => !current.includes(id))
    previousRunningRef.current = current
    // One that finished before its card was known is finished by that lookup instead.
    finished.filter((id) => cardByEvaluation[id] === undefined).forEach((id) => awaitingCardRef.current.add(id))
    const attributed = finished.filter((id) => cardByEvaluation[id] !== undefined)
    if (attributed.length === 0) {
      return
    }
    finishEvaluations(Object.fromEntries(attributed.map((id) => [id, cardByEvaluation[id]])))
  }, [runningIds, cardByEvaluation, finishEvaluations])

  const summaryCards = Object.keys(summaries).join(',')
  useEffect(() => {
    if (summaryCards === '') {
      return
    }
    const timer = setTimeout(() => {
      setSummaries({})
    }, SUMMARY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [summaryCards])

  const batchFor = useCallback(
    (card: CcqCard): CcqAiBatch => {
      const ofCard = running.filter((each) => cardByEvaluation[each.id] === card)
      if (ofCard.length > 0) {
        return {
          ...IDLE_BATCH,
          step: CcqAiBatchSteps.RUNNING,
          clientCount: ofCard.reduce((total, each) => total + each.clientCount, 0),
          isStopping: ofCard.every((each) => each.status === CrossClientQueryEvaluationStatuses.CANCELING || stoppingIds.includes(each.id)),
        }
      }
      return summaries[card] ?? IDLE_BATCH
    },
    [running, cardByEvaluation, summaries, stoppingIds],
  )

  // Rows started from this page show "AI checking" right away, before the next poll brings activeEvaluationId.
  const isChecking = useCallback((resultId: string) => running.some((each) => (checking[each.id] ?? []).includes(resultId)), [running, checking])

  const runClient = useCallback(
    async (result: CrossClientQueryResult, card: CcqCard) => {
      // The resolver passes still to come would overwrite what AI concludes.
      if (awaitsResolvers(execution, result, inFlight)) {
        return
      }
      const evaluation = await evaluateResults([result.id], CrossClientQueryEvaluationTypes.AI)
      if (evaluation) {
        trackEvaluation(evaluation, card, [result.id])
      }
    },
    [evaluateResults, trackEvaluation, execution, inFlight],
  )

  const stopBatch = useCallback(
    async (card: CcqCard) => {
      const ids = running
        .filter((each) => cardByEvaluation[each.id] === card && each.status !== CrossClientQueryEvaluationStatuses.CANCELING)
        .map((each) => each.id)
        .filter((id) => !stopRequestedRef.current.has(id))
      if (ids.length === 0) {
        return
      }
      ids.forEach((id) => stopRequestedRef.current.add(id))
      setStoppingIds((previous) => [...previous, ...ids])
      const outcomes = await Promise.allSettled(ids.map((evaluationId) => cancelEvaluation({ accountId, executionId: execution.id, evaluationId }).unwrap()))
      const failed = ids.filter((_, index) => outcomes[index].status === 'rejected')
      if (failed.length === 0) {
        return
      }
      failed.forEach((id) => stopRequestedRef.current.delete(id))
      setStoppingIds((previous) => previous.filter((id) => !failed.includes(id)))
    },
    [running, cardByEvaluation, cancelEvaluation, accountId, execution.id],
  )

  const value = useMemo<CcqAi>(
    () => ({ isChecking, runClient, batchFor, trackEvaluation, stopBatch }),
    [isChecking, runClient, batchFor, trackEvaluation, stopBatch],
  )

  return <CcqAiContext.Provider value={value}>{children}</CcqAiContext.Provider>
}
